import React, { useState, useEffect, useCallback } from 'react';
import { 
  Key,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Pause,
  Building,
  RefreshCw
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { adminApi } from '../../services/api';
import { CrossTenantApiKey, CrossTenantApiKeyFilters, PaginationInfo } from '../../types';
import { useLocale } from '../../context/LocaleContext';

const AllApiKeysPage: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<CrossTenantApiKey[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({ page: 1, limit: 20, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [filters, setFilters] = useState<CrossTenantApiKeyFilters>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fetchApiKeys = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminApi.getAllApiKeys({
        ...filters,
        search: searchTerm || undefined
      });
      setApiKeys(data.apiKeys);
      setPagination(data.pagination);
      setError(null);
    } catch (err) {
      setError('Failed to load API keys');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters, searchTerm]);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
    fetchApiKeys();
  };

  const handleFilterChange = (key: keyof CrossTenantApiKeyFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'inactive':
        return <Pause className="w-4 h-4 text-gray-500" />;
      case 'suspended':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'expired':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/10 text-green-500';
      case 'inactive':
        return 'bg-gray-500/10 text-gray-500';
      case 'suspended':
        return 'bg-red-500/10 text-red-500';
      case 'expired':
        return 'bg-yellow-500/10 text-yellow-500';
      default:
        return 'bg-gray-500/10 text-gray-500';
    }
  };

  const getTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'sdk':
        return 'bg-blue-500/10 text-blue-500';
      case 'integration':
        return 'bg-purple-500/10 text-purple-500';
      default:
        return 'bg-gray-500/10 text-gray-500';
    }
  };

  const { formatDate: formatLocaleDate, formatNumber: formatLocaleNumber } = useLocale();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return formatLocaleDate(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatNumber = (num: number) => {
    return formatLocaleNumber(num);
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-yellow-500/10 rounded-lg">
            <Key className="w-8 h-8 text-yellow-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold dark:text-white text-gray-900">
              All API Keys
            </h1>
            <p className="dark:text-text-secondary text-light-text-secondary">
              Cross-tenant API key management and monitoring
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchApiKeys()}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 dark:text-text-secondary text-light-text-secondary" />
                <input
                  type="text"
                  placeholder="Search API keys by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg dark:bg-background bg-gray-50 border dark:border-border-color border-light-border-color dark:text-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </form>

            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t dark:border-border-color border-light-border-color grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium dark:text-white text-gray-900 mb-1">
                  Status
                </label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                  className="w-full px-3 py-2 rounded-lg dark:bg-background bg-gray-50 border dark:border-border-color border-light-border-color dark:text-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium dark:text-white text-gray-900 mb-1">
                  Type
                </label>
                <select
                  value={filters.type || ''}
                  onChange={(e) => handleFilterChange('type', e.target.value || undefined)}
                  className="w-full px-3 py-2 rounded-lg dark:bg-background bg-gray-50 border dark:border-border-color border-light-border-color dark:text-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Types</option>
                  <option value="sdk">SDK</option>
                  <option value="integration">Integration</option>
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label className="block text-sm font-medium dark:text-white text-gray-900 mb-1">
                  Sort By
                </label>
                <select
                  value={filters.sortBy || 'createdAt'}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg dark:bg-background bg-gray-50 border dark:border-border-color border-light-border-color dark:text-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="createdAt">Created Date</option>
                  <option value="lastUsedAt">Last Used</option>
                  <option value="name">Name</option>
                  <option value="tenantId">Tenant</option>
                </select>
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium dark:text-white text-gray-900 mb-1">
                  Sort Order
                </label>
                <select
                  value={filters.sortOrder || 'desc'}
                  onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg dark:bg-background bg-gray-50 border dark:border-border-color border-light-border-color dark:text-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <p className="text-red-500">{error}</p>
          <Button variant="outline" size="sm" onClick={() => fetchApiKeys()} className="ml-auto">
            Retry
          </Button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* API Keys Table */}
      {!loading && !error && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b dark:border-border-color border-light-border-color">
                  <th className="text-left p-4 font-medium dark:text-text-secondary text-light-text-secondary">Name</th>
                  <th className="text-left p-4 font-medium dark:text-text-secondary text-light-text-secondary">Tenant</th>
                  <th className="text-left p-4 font-medium dark:text-text-secondary text-light-text-secondary">Type</th>
                  <th className="text-left p-4 font-medium dark:text-text-secondary text-light-text-secondary">Status</th>
                  <th className="text-left p-4 font-medium dark:text-text-secondary text-light-text-secondary">Usage</th>
                  <th className="text-left p-4 font-medium dark:text-text-secondary text-light-text-secondary">Last Used</th>
                  <th className="text-left p-4 font-medium dark:text-text-secondary text-light-text-secondary">Created</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((apiKey) => (
                  <tr
                    key={apiKey.apiKeyId}
                    className="border-b dark:border-border-color border-light-border-color hover:dark:bg-background hover:bg-gray-50 transition-colors"
                  >
                    <td className="p-4">
                      <div>
                        <p className="font-medium dark:text-white text-gray-900">{apiKey.name}</p>
                        {apiKey.description && (
                          <p className="text-sm dark:text-text-secondary text-light-text-secondary truncate max-w-xs">
                            {apiKey.description}
                          </p>
                        )}
                        <p className="text-xs font-mono dark:text-text-secondary text-light-text-secondary">
                          {apiKey.tokenMasked}
                        </p>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 dark:text-text-secondary text-light-text-secondary" />
                        <div>
                          <p className="font-medium dark:text-white text-gray-900">{apiKey.tenantName || apiKey.tenantId}</p>
                          {apiKey.tenantName && (
                            <p className="text-xs dark:text-text-secondary text-light-text-secondary">{apiKey.tenantId}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeBadgeClass(apiKey.type)}`}>
                        {apiKey.type?.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(apiKey.status)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(apiKey.status)}`}>
                          {apiKey.status}
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div>
                        <p className="font-medium dark:text-white text-gray-900">
                          {formatNumber(apiKey.usage?.totalRequests || 0)} total
                        </p>
                        <p className="text-sm dark:text-text-secondary text-light-text-secondary">
                          {formatNumber(apiKey.usage?.monthlyRequests || 0)} this month
                        </p>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="dark:text-white text-gray-900">{formatDate(apiKey.lastUsedAt)}</p>
                      {apiKey.usage?.lastRequestAt && (
                        <p className="text-xs dark:text-text-secondary text-light-text-secondary">
                          {new Date(apiKey.usage.lastRequestAt).toLocaleTimeString()}
                        </p>
                      )}
                    </td>
                    <td className="p-4">
                      <p className="dark:text-white text-gray-900">{formatDate(apiKey.createdAt)}</p>
                      {apiKey.createdBy && (
                        <p className="text-xs dark:text-text-secondary text-light-text-secondary">
                          by {apiKey.createdBy}
                        </p>
                      )}
                    </td>
                  </tr>
                ))}

                {apiKeys.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center dark:text-text-secondary text-light-text-secondary">
                      No API keys found matching your criteria
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between p-4 border-t dark:border-border-color border-light-border-color">
              <p className="text-sm dark:text-text-secondary text-light-text-secondary">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} API keys
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-3 py-1 dark:text-white text-gray-900">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default AllApiKeysPage;
